import { Buffer } from 'buffer'
import { offlineStorageService } from '../../sync/services/offlineStorage'

/**
 * PDF image reliability pipeline (RPT-02 / ADR B).
 *
 * react-pdf resolves image URLs at render time with no retry and no failure
 * signal — an expired signed URL or a dropped connection silently produces a
 * blank frame in the PDF. Instead, every image is materialised *before*
 * rendering starts:
 *
 *   offline blob (IndexedDB) → else fetch signed URL (3 attempts, backoff)
 *   → decode → downscale to ≤maxDimensionPx → JPEG 0.8 → data URL or bytes
 *
 * Downscaling also bounds memory: a 60-photo job of 12MP captures would
 * otherwise hold ~60 full-resolution bitmaps during render.
 */

const MAX_DIMENSION_PX = 1600

/**
 * Long-edge cap for a photo printed in a node card. The largest card cell is
 * ~250 × 250 pt (`photoCellWidth` / `PHOTO_MAX_H`, pinned by a unit test),
 * which is ~765 px at 220 dpi. Embedding at the 1600 px capture size instead
 * printed at ~480 dpi and made an 854-photo report 410 MB, more than iOS
 * Safari can hold while rendering.
 */
export const REPORT_PHOTO_MAX_PX = 800
export const REPORT_PHOTO_PRINT_DPI = 220

/**
 * Total encoded bytes for a report's photos, shared out evenly as each
 * photo's `maxBytes`. iOS kills Safari's page process at a hard 2048 MB
 * (iPhone 17, measured from the device log), and a report costs several
 * times its photo bytes to render, so the photos of an 854-photo report
 * must stay in the tens of MB. It also keeps the PDF under Supabase's 50 MB
 * upload limit, with room for the cover photo, site maps and text (45 MB
 * left an 854-photo report over that limit on a real job). Small reports
 * never reach it and keep the top rung.
 */
export const REPORT_PHOTO_BUDGET_BYTES = 38 * 1024 * 1024

/**
 * Encode settings a budgeted photo tries in order, until one fits its
 * `maxBytes`. The first rung is the unbudgeted print size; the last is
 * kept even when it is still over budget.
 */
export const REPORT_PHOTO_LADDER: ReadonlyArray<{ maxPx: number; quality: number }> = [
  { maxPx: REPORT_PHOTO_MAX_PX, quality: 0.8 },
  { maxPx: REPORT_PHOTO_MAX_PX, quality: 0.7 },
  { maxPx: 720, quality: 0.65 },
  { maxPx: 640, quality: 0.6 },
  { maxPx: 560, quality: 0.55 },
  { maxPx: 480, quality: 0.5 },
  { maxPx: 400, quality: 0.45 },
]
const JPEG_QUALITY = 0.8
const FETCH_ATTEMPTS = 3
const FETCH_CONCURRENCY = 4
const BACKOFF_BASE_MS = 500

export interface PdfImageTask {
  /** Stable key the caller uses to collect the result */
  key: string
  /** User-facing label used in error messages, e.g. "Tree 3, photo 2" */
  label: string
  signedUrl: string | null
  /** Photo ID to look up in the offline IndexedDB blob store first */
  offlinePhotoId?: string
  /**
   * Re-encode format. Use 'png' for line art with transparency (e.g. the
   * signature) — JPEG has no alpha channel and would render transparent
   * pixels as black.
   */
  format?: 'jpeg' | 'png'
  /** Composite onto this colour before encoding (flattens transparency) */
  background?: string
  /** Long-edge pixel cap; defaults to MAX_DIMENSION_PX */
  maxDimensionPx?: number
  /**
   * 'bytes' returns the encoded image as a Buffer instead of a data URL.
   * react-pdf takes a Buffer as an <Image> src directly, which skips the
   * base64 string (a third larger, held for the whole render) and react-pdf's
   * decode of it. Only for images drawn once: react-pdf keys its
   * per-document embed cache on a data URL, so a repeated image (the header
   * logo) must stay a data URL.
   *
   * Not a Blob: WebKit keeps every Blob's bytes in its shared Networking
   * process, not the page, until the page garbage-collects the (tiny) Blob
   * object. On a 288-tree, 854-photo report on an iPhone 17, the photo and
   * PDF Blobs took that process past its ~840 MB limit; iOS killed it and
   * Safari reloaded the page. So the 'bytes' path never creates a Blob: it
   * fetches into an ArrayBuffer, decodes via a data URL and encodes with
   * toDataURL.
   */
  output?: 'dataUrl' | 'bytes'
  /**
   * Byte budget for the encoded image ('bytes' output only): the photo steps
   * down REPORT_PHOTO_LADDER until it fits. See REPORT_PHOTO_BUDGET_BYTES.
   */
  maxBytes?: number
  /**
   * Decorative asset (e.g. the brand logo): failure to resolve drops the
   * image from the result map instead of failing the whole generation —
   * the renderer must have a fallback for a missing optional image.
   */
  optional?: boolean
}

export interface PdfImageResult {
  /** Set unless the task asked for `output: 'bytes'` */
  dataUrl?: string
  /** Set when the task asked for `output: 'bytes'` */
  bytes?: Buffer
  width: number
  height: number
}

export class ImageFetchError extends Error {
  constructor(
    public readonly failures: Array<{ label: string; reason: string }>,
    totalCount: number
  ) {
    super(
      `${failures.length} of ${totalCount} report image(s) could not be loaded: ` +
        failures.map((f) => f.label).join(', ')
    )
    this.name = 'ImageFetchError'
  }
}

async function fetchWithRetry(url: string, label: string, as: 'blob'): Promise<Blob>
async function fetchWithRetry(url: string, label: string, as: 'bytes'): Promise<ArrayBuffer>
async function fetchWithRetry(
  url: string,
  label: string,
  as: 'blob' | 'bytes'
): Promise<Blob | ArrayBuffer> {
  let lastError = 'unknown error'
  for (let attempt = 0; attempt < FETCH_ATTEMPTS; attempt++) {
    if (attempt > 0) {
      await new Promise((r) => setTimeout(r, BACKOFF_BASE_MS * 2 ** (attempt - 1)))
    }
    try {
      const response = await fetch(url)
      if (!response.ok) {
        lastError = `HTTP ${response.status}`
        // 4xx won't heal on retry (expired/invalid signature); fail fast
        if (response.status >= 400 && response.status < 500) break
        continue
      }
      return as === 'bytes' ? await response.arrayBuffer() : await response.blob()
    } catch (err) {
      lastError = err instanceof Error ? err.message : String(err)
    }
  }
  throw new Error(`${label}: ${lastError}`)
}

/**
 * Where budgeted photos start on REPORT_PHOTO_LADDER, shared by one
 * resolveImagesToDataUrls call. Photos from one job compress alike, so
 * starting where the last one fitted saves most re-encodes on a big job.
 */
interface LadderState {
  start: number
}

/**
 * Decode an image, downscale to the task's long-edge cap, re-encode.
 * EXIF orientation is applied during decode, so portrait captures keep their
 * portrait dimensions. Prefers createImageBitmap + OffscreenCanvas; falls
 * back to HTMLImageElement + <canvas> where those are unavailable.
 */
async function toScaledDataUrl(
  input: Blob | ArrayBuffer,
  task: PdfImageTask,
  ladder: LadderState = { start: 0 }
): Promise<PdfImageResult> {
  let width: number
  let height: number
  let source: ImageBitmap | HTMLImageElement

  if (!(input instanceof Blob)) {
    source = await decodeViaImageElement(
      `data:image/jpeg;base64,${Buffer.from(input).toString('base64')}`
    )
    width = source.naturalWidth
    height = source.naturalHeight
  } else if (typeof createImageBitmap === 'function') {
    try {
      // Explicitly bake in the EXIF rotation — without this a portrait phone
      // capture decodes as its landscape sensor buffer on some browsers
      source = await createImageBitmap(input, { imageOrientation: 'from-image' })
    } catch {
      // Older engines reject the options bag
      source = await createImageBitmap(input)
    }
    width = source.width
    height = source.height
  } else {
    source = await decodeViaImageElement(input)
    width = source.naturalWidth
    height = source.naturalHeight
  }

  const scale = Math.min(1, (task.maxDimensionPx ?? MAX_DIMENSION_PX) / Math.max(width, height))
  const targetW = Math.max(1, Math.round(width * scale))
  const targetH = Math.max(1, Math.round(height * scale))

  const format = task.format ?? 'jpeg'
  const mimeType = format === 'png' ? 'image/png' : 'image/jpeg'

  const draw = (ctx: OffscreenCanvasRenderingContext2D | CanvasRenderingContext2D) => {
    if (task.background) {
      ctx.fillStyle = task.background
      ctx.fillRect(0, 0, targetW, targetH)
    }
    ctx.drawImage(source, 0, 0, targetW, targetH)
  }

  if (task.output === 'bytes') {
    // Blob-free encode (see PdfImageTask.output)
    const canvas = document.createElement('canvas')
    const ctx = canvas.getContext('2d')
    if (!ctx) throw new Error('Could not acquire 2d canvas context')
    const encode = (maxPx: number, quality: number) => {
      const fit = Math.min(1, maxPx / Math.max(width, height))
      canvas.width = Math.max(1, Math.round(width * fit))
      canvas.height = Math.max(1, Math.round(height * fit))
      if (task.background) {
        ctx.fillStyle = task.background
        ctx.fillRect(0, 0, canvas.width, canvas.height)
      }
      ctx.drawImage(source, 0, 0, canvas.width, canvas.height)
      const base64 = canvas.toDataURL(mimeType, quality).split(',')[1] ?? ''
      return { base64, width: canvas.width, height: canvas.height }
    }

    let out: ReturnType<typeof encode>
    const budget = task.maxBytes
    if (budget == null) {
      out = encode(task.maxDimensionPx ?? MAX_DIMENSION_PX, JPEG_QUALITY)
    } else {
      const last = REPORT_PHOTO_LADDER.length - 1
      let rung = Math.min(ladder.start, last)
      for (;;) {
        const { maxPx, quality } = REPORT_PHOTO_LADDER[rung] ?? {
          maxPx: REPORT_PHOTO_MAX_PX,
          quality: JPEG_QUALITY,
        }
        out = encode(Math.min(maxPx, task.maxDimensionPx ?? maxPx), quality)
        const size = base64Bytes(out.base64)
        if (size <= budget || rung === last) {
          // Remember where photos fit; step back up when this one fitted
          // first time with room to spare, so one hard photo can't
          // lower every photo after it
          ladder.start = rung === ladder.start && size < budget * 0.6 ? Math.max(0, rung - 1) : rung
          break
        }
        rung++
      }
    }
    if ('close' in source) source.close()
    // Release the backing store now rather than at the next GC
    canvas.width = canvas.height = 0
    return { bytes: Buffer.from(out.base64, 'base64'), width: out.width, height: out.height }
  }

  let outBlob: Blob
  if (typeof OffscreenCanvas === 'function') {
    const canvas = new OffscreenCanvas(targetW, targetH)
    const ctx = canvas.getContext('2d')
    if (!ctx) throw new Error('Could not acquire 2d canvas context')
    draw(ctx)
    outBlob = await canvas.convertToBlob({ type: mimeType, quality: JPEG_QUALITY })
  } else {
    const canvas = document.createElement('canvas')
    canvas.width = targetW
    canvas.height = targetH
    const ctx = canvas.getContext('2d')
    if (!ctx) throw new Error('Could not acquire 2d canvas context')
    draw(ctx)
    outBlob = await new Promise<Blob>((resolve, reject) => {
      canvas.toBlob(
        (b) => (b ? resolve(b) : reject(new Error('canvas.toBlob returned null'))),
        mimeType,
        JPEG_QUALITY
      )
    })
  }

  if ('close' in source) source.close()

  return { dataUrl: await blobToDataUrl(outBlob), width: targetW, height: targetH }
}

/** Decoded size of a base64 string */
function base64Bytes(base64: string): number {
  const padding = base64.endsWith('==') ? 2 : base64.endsWith('=') ? 1 : 0
  return (base64.length * 3) / 4 - padding
}

/** Decodes a Blob (via an object URL) or a data URL */
function decodeViaImageElement(input: Blob | string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const url = typeof input === 'string' ? input : URL.createObjectURL(input)
    const release = () => {
      if (typeof input !== 'string') URL.revokeObjectURL(url)
    }
    const img = new Image()
    img.onload = () => {
      release()
      resolve(img)
    }
    img.onerror = () => {
      release()
      reject(new Error('Image decode failed'))
    }
    img.src = url
  })
}

function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(reader.result as string)
    reader.onerror = () => reject(new Error('Failed to read image data'))
    reader.readAsDataURL(blob)
  })
}

async function resolveOne(task: PdfImageTask, ladder: LadderState): Promise<PdfImageResult> {
  // Offline-captured photos still awaiting sync live in IndexedDB — use the
  // local blob and skip the network entirely
  if (task.offlinePhotoId) {
    try {
      const localBlob = await offlineStorageService.getPhotoBlob(task.offlinePhotoId)
      if (localBlob) return await toScaledDataUrl(localBlob, task, ladder)
    } catch {
      // IndexedDB unavailable (private browsing etc.) — fall through to network
    }
  }

  if (!task.signedUrl) {
    throw new Error(`${task.label}: no image URL available`)
  }

  const fetched =
    task.output === 'bytes'
      ? await fetchWithRetry(task.signedUrl, task.label, 'bytes')
      : await fetchWithRetry(task.signedUrl, task.label, 'blob')
  return toScaledDataUrl(fetched, task, ladder)
}

/**
 * Resolve every task to a data URL (with pixel dimensions) with bounded
 * concurrency. Throws ImageFetchError listing every failed image (not just
 * the first), so the user sees the full repair list in one pass.
 */
export async function resolveImagesToDataUrls(
  tasks: PdfImageTask[],
  onProgress?: (done: number, total: number) => void
): Promise<Map<string, PdfImageResult>> {
  const results = new Map<string, PdfImageResult>()
  const failures: Array<{ label: string; reason: string }> = []
  let done = 0
  let cursor = 0
  const ladder: LadderState = { start: 0 }

  const worker = async () => {
    while (cursor < tasks.length) {
      const task = tasks[cursor++]
      if (!task) break
      try {
        results.set(task.key, await resolveOne(task, ladder))
      } catch (err) {
        if (!task.optional) {
          failures.push({
            label: task.label,
            reason: err instanceof Error ? err.message : String(err),
          })
        }
      } finally {
        done++
        onProgress?.(done, tasks.length)
      }
    }
  }

  await Promise.all(
    Array.from({ length: Math.min(FETCH_CONCURRENCY, tasks.length) }, () => worker())
  )

  if (failures.length > 0) {
    throw new ImageFetchError(failures, tasks.length)
  }

  return results
}
